<template>
  <GamConfirmModal
    :title="localize(content.titleKey)"
    :keep-label="localize(content.ctaKey)"
    wide
    @close="emit('close')"
    @keep="handleCta"
  >
    <p v-if="intro" class="lhm-intro">
      <GamIconText :text="intro" />
    </p>
    <ol class="lhm-steps" :class="{ single: content.steps.length === 1 }">
      <li v-for="line in content.steps" :key="line.key">
        <GamIconText :text="render(line)" />
      </li>
    </ol>
    <ul v-if="content.notes.length" class="lhm-notes">
      <li v-for="line in content.notes" :key="line.key">
        <GamIconText :text="render(line)" />
      </li>
    </ul>
  </GamConfirmModal>
</template>

<script lang="ts" setup>
import { localize, localizeWithValues } from '@/core/gambits';
import {
  buildLocationHelpContent,
  detectLocationHelpClient,
  type LocationHelpLine,
} from '@/core/helpers/location-help.helper';
import { isPWA } from '@/core/helpers/ui.helper';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamIconText from '@/views/components/GamIconText.vue';
import { LocationHelpKind } from '@/views/composables/constants/main/locationHelp.constants';
import { locationAskSentenceKey, locationHelpReason } from '@/views/composables/hooks/useLocationHelp';
import { computed } from 'vue';

const props = defineProps<{ kind: LocationHelpKind }>();

const emit = defineEmits<{ close: [] }>();

const client = detectLocationHelpClient(navigator.userAgent, navigator.maxTouchPoints, isPWA.value);

const content = computed(() => buildLocationHelpContent(props.kind, client, window.location.host));

const intro = computed(() => {
  const ask = locationHelpReason.value;
  if (ask && (props.kind === LocationHelpKind.BLOCKED || props.kind === LocationHelpKind.UNAVAILABLE)) {
    return localize(locationAskSentenceKey(ask));
  }
  if (props.kind === LocationHelpKind.BLOCKED) return '';
  return localize(content.value.introKey);
});

function render(line: LocationHelpLine): string {
  return line.values ? localizeWithValues(line.key, [...line.values]) : localize(line.key);
}

function handleCta() {
  if (content.value.reloads) {
    window.location.reload();
    return;
  }
  emit('close');
}
</script>

<style lang="scss" scoped>
.lhm-intro,
.lhm-steps,
.lhm-notes {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-white-70);
  text-align: left;
}

.lhm-steps {
  margin-top: var(--spacing-md);
  padding-left: 1.25rem;

  li + li {
    margin-top: var(--spacing-xs);
  }

  &.single {
    list-style: none;
    padding-left: 0;
  }
}

.lhm-notes {
  margin-top: var(--spacing-md);
  padding: var(--spacing-md) 0 0;
  border-top: 1px solid var(--color-white-10);
  list-style: none;
  font-size: 13px;

  li + li {
    margin-top: var(--spacing-xs);
  }
}
</style>
